import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  createContext,
  useContext,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import ZoomMtgEmbedded, {
  InitOptions,
  SuspensionViewType,
} from '@zoom/meetingsdk/embedded'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import type {
  ZoomHostMeetingResponse,
  ZoomConnectionState,
  ZoomJoinOptions,
  ZoomIntegrationStatus,
} from '@/types/meeting-integrations'
import { apiService } from '@/service/api'
import { useAppStore } from '@/store'

interface ZoomMeetingContextValue {
  startMeeting: (agentId?: string) => Promise<void>
  isMeetingInProgress: boolean
  zoomStatus: ZoomIntegrationStatus
}

const ZoomMeetingContext = createContext<ZoomMeetingContextValue | null>(null)

// Default no-op value for when the hook is called outside the provider.
// This avoids a crash when components using this hook render before
// the ZoomMeeting provider mounts (e.g., during route transitions).
const defaultControls: ZoomMeetingContextValue = {
  startMeeting: async () => {},
  isMeetingInProgress: false,
  zoomStatus: 'unlinked',
}

// eslint-disable-next-line react-refresh/only-export-components
export const useZoomMeetingControls = (): ZoomMeetingContextValue => {
  const ctx = useContext(ZoomMeetingContext)
  return ctx ?? defaultControls
}

interface ZoomMeetingProps {
  children?: ReactNode
}

const ZoomMeeting = ({ children }: ZoomMeetingProps) => {
  const { t } = useTranslation()
  const [client] = useState(() => ZoomMtgEmbedded.createClient())
  const [isMeetingInProgress, setIsMeetingInProgress] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null)
  const meetingContainerRef = useRef<HTMLDivElement | null>(null)
  const hasRequestedStatusRef = useRef(false)
  const contextId = useAppStore((state) => state.context_id)
  const zoomStatus = useAppStore((state) => state.zoomStatus)
  const fetchMeetingIntegrationsStatus = useAppStore(
    (state) => state.fetchMeetingIntegrationsStatus
  )
  const selectedAgentForCall = useAppStore(
    (state) => state.selectedAgentForCall
  )
  const selectedAgent = useAppStore((state) => state.selectedAgent)
  const agents = useAppStore((state) => state.agents)
  const userName = useAppStore((state) => state.user?.name ?? 'User')

  useEffect(() => {
    if (hasRequestedStatusRef.current) return
    fetchMeetingIntegrationsStatus()
      .catch(() => undefined)
      .finally(() => {
        hasRequestedStatusRef.current = true
      })
  }, [fetchMeetingIntegrationsStatus])

  useEffect(() => {
    setPortalRoot(document.body)
  }, [])

  const initializeZoomClient = useCallback(
    async (meetingSDKElement: HTMLElement) => {
      if (isInitialized) return

      try {
        const initOptions: InitOptions = {
          zoomAppRoot: meetingSDKElement,
          language: 'en-US',
          customize: {
            video: {
              defaultViewType: 'gallery' as SuspensionViewType,
              isResizable: true,
              // viewSizes: {
              //   default: {
              //     width: 600,
              //     height: 400
              //   },
              //   ribbon: {
              //     width: 600,
              //     height: 400
              //   }
              // }
            },
          },
          patchJsMedia: true,
          leaveOnPageUnload: true,
        }

        await client.init(initOptions)

        // Set up event listener for meeting end
        client.on('connection-change', (payload: ZoomConnectionState) => {
          console.log('Connection state changed:', payload.state)
          if (payload.state === 'Closed') {
            console.log('Meeting ended')
            setIsMeetingInProgress(false)
          }
        })

        setIsInitialized(true)
      } catch (error) {
        console.error('Error initializing Zoom client:', error)
        toast.error(t('common:zoom.errors.failed_to_initialize'))
      }
    },
    [client, isInitialized, t]
  )

  const resolveAgentId = useCallback(
    (explicitAgentId?: string) => {
      if (explicitAgentId) return explicitAgentId
      if (selectedAgentForCall?.availableForMeeting)
        return selectedAgentForCall.id
      if (selectedAgent?.availableForMeeting) return selectedAgent.id
      const fallbackAgent = agents.find((agent) => agent.availableForMeeting)
      return fallbackAgent?.id ?? null
    },
    [agents, selectedAgent, selectedAgentForCall]
  )

  const startMeeting = useCallback(
    async (agentId?: string) => {
      if (zoomStatus !== 'linked') {
        toast.error(t('common:zoom.errors.account_not_linked'))
        return
      }

      const resolvedAgentId = resolveAgentId(agentId)
      if (!resolvedAgentId) {
        toast.error(t('common:zoom.errors.no_meeting_agent'))
        return
      }

      const meetingSDKElement = meetingContainerRef.current
      if (!meetingSDKElement) {
        toast.error(t('common:zoom.errors.missing_container'))
        return
      }

      try {
        const data: ZoomHostMeetingResponse =
          await apiService.startZoomMeeting()

        // Extract meeting details from response
        const { signature, meetingDetails, zakToken } = data
        const meetingNumber = meetingDetails.id.toString()
        const password = meetingDetails.password
        const userEmail = meetingDetails.host_email

        // Initialize client if not already initialized
        if (!isInitialized) {
          await initializeZoomClient(meetingSDKElement)
        }

        setIsMeetingInProgress(true)

        const meetingWebAppUrl = `${window.location.origin}/meeting`

        const joinOptions: ZoomJoinOptions = {
          signature: signature,
          meetingNumber: meetingNumber,
          password: password,
          userName: userName,
          userEmail: userEmail,
          zak: zakToken,
        }

        await client.join(joinOptions)
        await apiService.startMeeting(
          meetingDetails.join_url,
          meetingWebAppUrl,
          resolvedAgentId,
          contextId
        )
        console.log('Joined meeting successfully')
        await fetchMeetingIntegrationsStatus().catch(() => undefined)
      } catch (error) {
        console.error('Error starting meeting:', error)
        toast.error(t('common:zoom.errors.failed_to_start'))
        setIsMeetingInProgress(false)
      }
    },
    [
      zoomStatus,
      isInitialized,
      client,
      contextId,
      initializeZoomClient,
      fetchMeetingIntegrationsStatus,
      resolveAgentId,
      userName,
      t,
    ]
  )

  const contextValue = useMemo(
    () => ({
      startMeeting,
      isMeetingInProgress,
      zoomStatus,
    }),
    [startMeeting, isMeetingInProgress, zoomStatus]
  )

  const meetingOverlay =
    portalRoot &&
    createPortal(
      <div
        className={`fixed inset-0 flex items-center justify-center transition-opacity duration-150 ${isMeetingInProgress
          ? 'opacity-100 pointer-events-auto'
          : 'opacity-0 pointer-events-none'
          }`}
      >
        <div
          id="meetingSDKElement"
          ref={meetingContainerRef}
          className="rounded-xl shadow-2xl flex items-center justify-center bg-white dark:bg-dark-card"
        />
      </div>,
      portalRoot
    )

  return (
    <ZoomMeetingContext.Provider value={contextValue}>
      {children}
      {meetingOverlay}
    </ZoomMeetingContext.Provider>
  )
}

export default ZoomMeeting
